import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import Button from '../../../components/atoms/button';
import Card from '../../../components/atoms/card';
import SVGIcon from '../../../components/atoms/icons';
import Select from '../../../components/atoms/select';
import { NotificationToast } from '../../../components/organisms/notification-toast';
import ContainerService, {
	FormatoExportacaoScrollBack,
	IContainer,
	IContainerScrollBack,
} from '../../../services/Container.service';
import YardService, { IYard } from '../../../services/Yard.service';
import { HomeLoaderData } from '../../../types/homeLoaderData';
import { EditableChangeEvent } from '../../../utils/types/editable';

const ScrollBackAuditoria = () => {
	const { accountId, user } = useRouteLoaderData('home') as HomeLoaderData;
	const [patios, setPatios] = useState<IYard[]>([]);
	const [containers, setContainers] = useState<IContainer[]>([]);
	const [patioSelecionado, setPatioSelecionado] = useState<IYard>();
	const [containerSelecionado, setContainerSelecionado] = useState('');
	const [resultado, setResultado] = useState<IContainerScrollBack>();
	const [carregando, setCarregando] = useState(false);
	const [formatoExportando, setFormatoExportando] =
		useState<FormatoExportacaoScrollBack>();
	const supplyReturnAtivado =
		user.client?.system_configs?.supplyReturnAtivado === true;

	const formatarOpcaoContainer = (container: IContainer) =>
		container.carrier
			? `${container.label} - ${container.carrier}`
			: container.label;

	const buscarPatios = useCallback(async () => {
		const resposta = await YardService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		setPatios(resposta?.data || []);
	}, [accountId]);

	useEffect(() => {
		buscarPatios();
	}, [buscarPatios]);

	const lidarComPatio = async (event: EditableChangeEvent) => {
		const patio = patios.find((item) => item.name === event.target.value);
		setPatioSelecionado(patio);
		setContainerSelecionado('');
		setResultado(undefined);
		setContainers([]);

		if (!patio?.id) return;
		const resposta = await ContainerService.getAllByYard(patio.id);
		setContainers(resposta || []);
	};

	const lidarComContainer = (event: EditableChangeEvent) => {
		const container = containers.find(
			(item) => formatarOpcaoContainer(item) === String(event.target.value)
		);
		setContainerSelecionado(container?.label || '');
		setResultado(undefined);
	};

	const buscarScrollBack = async () => {
		if (!patioSelecionado?.id || !containerSelecionado) return;

		setCarregando(true);
		const resposta = await ContainerService.getScrollBack(
			containerSelecionado,
			patioSelecionado.id
		);
		setCarregando(false);

		if (!resposta) {
			setResultado(undefined);
			NotificationToast.Warn('Não foi possível localizar o ScrollBack deste container.');
			return;
		}

		setResultado(resposta);
	};

	const exportarScrollBack = async (formato: FormatoExportacaoScrollBack) => {
		if (!patioSelecionado?.id || !containerSelecionado) return;

		setFormatoExportando(formato);
		const arquivo = await ContainerService.exportarScrollBack(
			containerSelecionado,
			patioSelecionado.id,
			formato
		);
		setFormatoExportando(undefined);

		if (!arquivo) {
			NotificationToast.Warn(
				`Não foi possível exportar o ScrollBack em ${formato === 'pdf' ? 'PDF' : 'Excel'}.`
			);
			return;
		}

		const urlArquivo = URL.createObjectURL(arquivo);
		const linkDownload = document.createElement('a');
		linkDownload.href = urlArquivo;
		linkDownload.download = `scrollback-${containerSelecionado}.${formato}`;
		document.body.appendChild(linkDownload);
		linkDownload.click();
		linkDownload.remove();
		URL.revokeObjectURL(urlArquivo);
	};

	const formatarDataHora = (data?: string | null) => {
		if (!data) return '-';
		return new Intl.DateTimeFormat('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'America/Manaus',
		}).format(new Date(data));
	};

	const formatarValor = (valor?: string | number | boolean | null) => {
		if (valor === null || valor === undefined || valor === '') return '-';
		if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
		return valor;
	};

	const formatarTemperatura = (valor?: number | null) =>
		valor === null || valor === undefined ? '-' : `${valor}°C`;

	const formatarProblemas = (problemas?: number[] | null) => {
		const rotulos: Record<number, string> = {
			2: 'CoolingDown',
			3: 'Defrost',
			4: 'Problema Crítico',
			6: 'Sem Alimentação',
			8: 'Falta de Energia',
			10: 'Manutenção Preventiva',
			12: 'Baixo Rendimento',
			13: 'Temperatura Crítica',
		};

		return problemas?.length
			? problemas.map((problema) => rotulos[problema] || `#${problema}`).join(', ')
			: '-';
	};

	const renderizarTabela = (
		titulo: string,
		linhas: { rotulo: string; valor: ReactNode }[]
	) => (
		<section className="mt-8">
			<h3 className="mb-3 text-center text-base font-bold text-[#222]">
				{titulo}
			</h3>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[680px] border-collapse text-xs text-[#222]">
					<tbody>
						{linhas.map((linha) => (
							<tr key={linha.rotulo}>
								<th className="w-56 border border-[#222] bg-[#eef2f7] px-3 py-2 text-left font-bold">
									{linha.rotulo}
								</th>
								<td className="border border-[#222] px-3 py-2">
									{linha.valor}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);

	const containerSelecionadoDados = containers.find(
		(container) => container.label === containerSelecionado
	);

	return (
		<div className="flex flex-col gap-6">
			<Card className="grid gap-5 p-6 md:grid-cols-[1fr_1fr_auto] md:items-end">
				<label className="flex flex-col gap-2 font-semibold text-primary">
					Pátio:
					<Select
						boxClassName="h-fit"
						onChange={lidarComPatio}
						options={patios.map((patio) => patio.name)}
						placeholder="Selecione o pátio"
						value={patioSelecionado?.name || ''}
					/>
				</label>
				<label className="flex flex-col gap-2 font-semibold text-primary">
					Container:
					<Select
						boxClassName="h-fit"
						disabled={!patioSelecionado}
						onChange={lidarComContainer}
						options={containers.map(formatarOpcaoContainer)}
						placeholder="Selecione o container"
						value={
							containerSelecionadoDados
								? formatarOpcaoContainer(containerSelecionadoDados)
								: ''
						}
					/>
				</label>
				<Button
					className="h-11 px-6"
					disabled={!patioSelecionado?.id || !containerSelecionado}
					loading={carregando}
					onClick={buscarScrollBack}
				>
					Buscar
				</Button>
			</Card>

			{resultado && (
				<div className="rounded-lg p-3 sm:p-6">
					<div className="mx-auto mb-4 flex max-w-5xl justify-end gap-3">
						<Button
							loading={formatoExportando === 'pdf'}
							disabled={Boolean(formatoExportando)}
							onClick={() => exportarScrollBack('pdf')}
							title="Baixar ScrollBack em PDF"
						>
							<svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24">
								<path d="M6 2h8l4 4v16H6V2Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
								<path d="M14 2v5h4M8.5 12h7M8.5 16h5" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
							</svg>
							PDF
						</Button>
						<Button
							loading={formatoExportando === 'xlsx'}
							disabled={Boolean(formatoExportando)}
							onClick={() => exportarScrollBack('xlsx')}
							title="Baixar ScrollBack em Excel"
						>
							<svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24">
								<path d="M4 3h16v18H4V3Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
								<path d="M4 9h16M4 15h16M10 3v18" stroke="currentColor" strokeWidth="2" />
							</svg>
							Excel
						</Button>
					</div>
					<article className="mx-auto max-w-5xl bg-white px-5 py-8 text-[#222] shadow-xl sm:px-10">
						<header className="text-center">
							<img
								alt="Inova Reefer"
								className="mx-auto h-auto w-[150px]"
								src="https://reefersys-v2-assets.s3.us-east-1.amazonaws.com/logo-inova-reefer.png"
							/>
							<p className="text-xs font-semibold text-primary">
								CNPJ: 57.456.341/0001-93
							</p>
							<h2 className="mt-6 text-xl font-bold uppercase">
								Relatório de Auditoria
							</h2>
							<p className="mt-2 text-xs text-[#555]">
								Histórico operacional do container {resultado.label} - {resultado.carrier}
							</p>
						</header>

						<div className="mt-8 overflow-x-auto">
							<table className="w-full min-w-[760px] border-collapse text-center text-xs">
								<thead>
									<tr>
										{(supplyReturnAtivado
											? ['Container', 'SetPoint', 'Supply', 'Return', 'Pátio', 'Navio', 'Posição']
											: ['Container', 'SetPoint', 'Temperatura', 'Pátio', 'Navio', 'Posição']
										).map((titulo) => (
											<th className="border border-[#222] bg-[#eef2f7] px-3 py-2" key={titulo}>
												{titulo}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-[#222] px-3 py-2">{resultado.label} - {resultado.carrier}</td>
										<td className="border border-[#222] px-3 py-2">{formatarTemperatura(resultado.temperature)}</td>
										{supplyReturnAtivado ? (
											<>
												<td className="border border-[#222] px-3 py-2">{formatarTemperatura(resultado.monitorings[resultado.monitorings.length - 1]?.supply)}</td>
												<td className="border border-[#222] px-3 py-2">{formatarTemperatura(resultado.monitorings[resultado.monitorings.length - 1]?.return)}</td>
											</>
										) : (
											<td className="border border-[#222] px-3 py-2">{formatarTemperatura(resultado.monitorings[resultado.monitorings.length - 1]?.temperature)}</td>
										)}
										<td className="border border-[#222] px-3 py-2">{resultado.movement.yard.name}</td>
										<td className="border border-[#222] px-3 py-2">{resultado.movement.ship.name} — {resultado.movement.ship.owner} | {resultado.movement.travelCode}</td>
										<td className="border border-[#222] px-3 py-2">{formatarValor(resultado.position)}</td>
									</tr>
								</tbody>
							</table>
						</div>

						{renderizarTabela('Identificação', [
							{ rotulo: 'ID', valor: resultado.id },
							{ rotulo: 'Container', valor: `${resultado.label} - ${resultado.carrier}` },
							{ rotulo: 'Armador', valor: formatarValor(resultado.carrier) },
							{ rotulo: 'Cliente', valor: formatarValor(resultado.owner) },
							{ rotulo: 'Responsável', valor: formatarValor(resultado.ownerUser) },
							{ rotulo: 'Posição', valor: formatarValor(resultado.position) },
							{ rotulo: 'Pátio', valor: resultado.movement.yard.name },
							{ rotulo: 'Navio', valor: `${resultado.movement.ship.name} — ${resultado.movement.ship.owner} | ${resultado.movement.travelCode}` },
							{ rotulo: 'Código da viagem', valor: resultado.movement.travelCode },
							{ rotulo: 'Entrada', valor: formatarDataHora(resultado.dateArrive) },
							{ rotulo: 'Saída', valor: formatarDataHora(resultado.dateLeave) },
							{ rotulo: 'No pátio', valor: formatarValor(resultado.isArrive) },
							{ rotulo: 'Saiu do pátio', valor: formatarValor(resultado.isLeave) },
						])}

						{renderizarTabela('Dados Operacionais', [
							{ rotulo: 'SetPoint', valor: formatarTemperatura(resultado.temperature) },
							{ rotulo: 'Umidade', valor: formatarValor(resultado.humidity) },
							{ rotulo: 'Ventilação', valor: formatarValor(resultado.ventilation) },
							{ rotulo: 'Atmosfera controlada', valor: formatarValor(resultado.atmosferaControlada) },
							{ rotulo: 'Nível de CO₂', valor: formatarValor(resultado.nivelCo2) },
							{ rotulo: 'Return', valor: formatarValor(resultado.return) },
							{ rotulo: 'Supply', valor: formatarValor(resultado.supply) },
							{ rotulo: 'Cadastro', valor: formatarDataHora(resultado.createdAt) },
							{ rotulo: 'Última atualização', valor: formatarDataHora(resultado.updatedAt) },
						])}

						<section className="mt-8">
							<h3 className="mb-3 text-center text-base font-bold">Histórico de Monitoramentos</h3>
							<div className="overflow-x-auto">
								<table className="w-full min-w-[910px] border-collapse text-center text-xs">
									<thead>
										<tr>
											{(supplyReturnAtivado
												? ['ID', 'Data', 'Supply', 'Return', 'Umidade', 'Ventilação', 'Atmosfera controlada', 'CO₂', 'Return', 'Supply', 'Problemas', 'Observação']
												: ['ID', 'Data', 'Temperatura', 'Umidade', 'Ventilação', 'Atmosfera controlada', 'CO₂', 'Return', 'Supply', 'Problemas', 'Observação']
											).map((titulo) => (
												<th className="border border-[#222] bg-[#eef2f7] px-2 py-2" key={titulo}>{titulo}</th>
											))}
										</tr>
									</thead>
									<tbody>
										{resultado.monitorings.map((monitoramento) => (
											<tr key={monitoramento.id}>
												<td className="border border-[#222] px-2 py-2">{monitoramento.id}</td>
												<td className="border border-[#222] px-2 py-2">{formatarDataHora(monitoramento.date)}</td>
												{supplyReturnAtivado ? (
													<>
														<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.supply)}</td>
														<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.return)}</td>
													</>
												) : (
													<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.temperature)}</td>
												)}
												<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.humidity)}</td>
												<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.ventilation)}</td>
												<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.atmosferaControlada)}</td>
												<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.nivelCo2)}</td>
												<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.return)}</td>
												<td className="border border-[#222] px-2 py-2">{formatarValor(monitoramento.supply)}</td>
												<td className="border border-[#222] px-2 py-2">{formatarProblemas(monitoramento.problems)}</td>
												<td className="border border-[#222] px-2 py-2 text-left">{formatarValor(monitoramento.observation)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{!resultado.monitorings.length && <p className="py-5 text-center text-sm">Nenhum monitoramento encontrado.</p>}
						</section>

						<section className="mt-8">
							<h3 className="mb-3 text-center text-base font-bold">Histórico de Manutenções</h3>
							{resultado.maintenances.map((manutencao) => (
								<div className="mb-6" key={manutencao.id}>
									{renderizarTabela(`Manutenção #${manutencao.id}`, [
										{ rotulo: 'Data', valor: formatarDataHora(manutencao.date) },
										{ rotulo: 'Tipos', valor: manutencao.maintenance_types?.length ? manutencao.maintenance_types.map((tipo) => `#${tipo}`).join(', ') : '-' },
										{ rotulo: 'Descrição', valor: formatarValor(manutencao.description) },
										{ rotulo: 'HH', valor: formatarValor(manutencao.hh) },
										{ rotulo: 'Voltagem', valor: formatarValor(manutencao.voltage) },
										{ rotulo: 'Tamanho', valor: formatarValor(manutencao.size) },
										{ rotulo: 'Fabricante', valor: formatarValor(manutencao.manufactory) },
										{ rotulo: 'Bloco', valor: formatarValor(manutencao.block) },
										{ rotulo: 'Lote', valor: formatarValor(manutencao.batch) },
										{ rotulo: 'Local', valor: formatarValor(manutencao.place) },
										{ rotulo: 'Materiais', valor: manutencao.materials.length ? manutencao.materials.map((material) => `#${material.id} (${material.quantity})`).join(', ') : '-' },
									])}
								</div>
							))}
							{!resultado.maintenances.length && <p className="py-5 text-center text-sm">Nenhuma manutenção encontrada.</p>}
						</section>

						{(resultado.monitorings.some((item) => item.images?.length) || resultado.maintenances.some((item) => item.images?.length)) && (
							<section className="mt-8">
								<h3 className="mb-3 text-center text-base font-bold">Evidências</h3>
								<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
									{[
										...resultado.monitorings.flatMap((item) => item.images || []),
										...resultado.maintenances.flatMap((item) => item.images || []),
									].map((imagem, indice) => (
										<a className="overflow-hidden border border-[#222]" href={imagem} key={`${imagem}-${indice}`} rel="noreferrer" target="_blank">
											<img alt={`Evidência ${indice + 1}`} className="h-40 w-full object-cover" src={imagem} />
										</a>
									))}
								</div>
							</section>
						)}

						<footer className="mt-10 border-t border-[#bbb] pt-5 text-center text-xs text-[#555]">
							<SVGIcon className="mx-auto h-auto w-44" icon="LogoColored" />
							<p className="mt-2">Relatório gerado pelo sistema ReeferSys.</p>
						</footer>
					</article>
				</div>
			)}
		</div>
	);
};

export default ScrollBackAuditoria;
